import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import './courier-details.scss';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import orderServices from '../../services/orders/orderservice';
import courierDetailsService, { CourierDetailsDTO as ServiceDTO } from '../../service/courier-details-service';



interface CourierDetailsDTO extends ServiceDTO {
    courierByName: string | null;
    uniqueKey?: string;
}

interface AdminUserOption {
    userID: number;
    name: string;
    primaryContactNbr: string;
    roleID?: number;
}


interface AdminUserResult {
    userID: number;
    detail: string;
}

// ─── CourierByDropdown lives OUTSIDE CourierDetails so React never sees it
// as a "new" component type on re-renders, preventing unwanted unmount/remount.
interface CourierByDropdownProps {
    rowData: CourierDetailsDTO;
    onUpdateCourierBy: (uniqueKey: string, userId: number, name: string | null) => void;
}

const CourierByDropdown: React.FC<CourierByDropdownProps> = ({ rowData, onUpdateCourierBy }) => {

    const blank: AdminUserOption[] = [
        { userID: 0, name: 'Search by User/Mobile', primaryContactNbr: '' }
    ];

    const [admins, setAdmins] = useState<AdminUserOption[]>(blank);
    const [list, setList] = useState<AdminUserResult[]>([]);

    const handleFilter = (e: DropdownFilterEvent) => {
        if (e.filter.length > 2) {
            orderServices.getadminuserdetails(e.filter)
                .then(res => {
                    if (res?.length) {
                        setAdmins(res);
                    } else {
                        setAdmins(blank);
                    }
                })
                .catch(() => setAdmins(blank));
        }
    };

    const handleChange = (e: DropdownChangeEvent) => {
        const id = e.value;
        if (!id) {
            onUpdateCourierBy(rowData.uniqueKey!, 0, null);
            return;
        }
        const admin = admins.find(a => a.userID === id);
        if (admin) {
            onUpdateCourierBy(rowData.uniqueKey!, admin.userID, admin.name);
        }
    };

    useEffect(() => {
        let temp: AdminUserResult[] = [];
        if (rowData.courieredByID && rowData.courierByName) {
            temp.push({ userID: rowData.courieredByID, detail: rowData.courierByName });
        }
        admins.forEach(a => {
            if (!temp.find(x => x.userID === a.userID)) {
                temp.push({ userID: a.userID, detail: `${a.name}, ${a.primaryContactNbr}` });
            }
        });
        setList(temp);
    }, [admins, rowData.courieredByID, rowData.courierByName]);

    return (
        <Dropdown
            appendTo={document.body}
            options={list}
            optionLabel="detail"
            optionValue="userID"
            value={rowData.courieredByID || null}
            onChange={handleChange}
            onFilter={handleFilter}
            filter
            showClear
            placeholder="Select Admin *"
            style={{
                width: '100%',
                borderColor: !rowData.courieredByID ? '#ef4444' : undefined,
                borderWidth: !rowData.courieredByID ? '2px' : undefined
            }}
        />
    );
};

const CourierDetails: React.FC = () => {
    const [data, setData] = useState<CourierDetailsDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<CourierDetailsDTO[]>([]);
    const toast = useRef<Toast>(null);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [withoutTrackingID, setWithoutTrackingID] = useState<boolean>(false);
    const savingRef = useRef<Record<string, boolean>>({});
    const sendingRef = useRef<boolean>(false);

    // Sort state (client-side, applied to current page data)
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<1 | -1>(1);

    // Courier provider filter (MultiSelect over current page)
    const [courierProviderFilter, setCourierProviderFilter] = useState<string[]>([]);

    // Scroll state – used to show/hide the frozen column edge indicator
    const [isTableScrolled, setIsTableScrolled] = useState(false);
    const tableWrapperRef = useRef<HTMLDivElement>(null);

    // Attach scroll listener to DataTable's inner scrollable body
    useEffect(() => {
        const attachScrollListener = () => {
            const scrollBody = tableWrapperRef.current?.querySelector('.p-datatable-wrapper') as HTMLElement | null;
            if (!scrollBody) return;
            const handleScroll = () => setIsTableScrolled(scrollBody.scrollLeft > 0);
            scrollBody.addEventListener('scroll', handleScroll, { passive: true });
            return () => scrollBody.removeEventListener('scroll', handleScroll);
        };
        // Wait one tick for DataTable to render its DOM
        const timer = setTimeout(attachScrollListener, 100);
        return () => clearTimeout(timer);
    }, [data]);


    useEffect(() => {
        loadData();
    }, [
        currentPage,
        pageSize,
        fromDate,
        toDate,
        searchKeyword,
        withoutTrackingID
    ]);




    useEffect(() => {
        setSelectedRows([]);
    }, [withoutTrackingID]);


    // Debug: Monitor data state changes
    useEffect(() => {
        console.log('📊 DATA STATE CHANGED:', {
            length: data.length,
            records: data.map(d => ({
                orderID: d.orderID,
                customerName: d.customerName,
                invoiceNo: d.invoiceNo
            }))
        });
    }, [data]);


    const loadData = async () => {
        console.log('🚀 loadData CALLED with params:', {
            fromDate: fromDate?.toISOString(),
            toDate: toDate?.toISOString(),
            searchKeyword,
            withoutTrackingID,
            currentPage,
            pageSize
        });

        setLoading(true);

        try {
            const fromDateStr = fromDate ? toLocalISODate(fromDate) : undefined;
            const toDateStr = toDate ? toLocalISODate(toDate) : undefined;

            console.log('📡 Calling API with:', {
                fromDateStr,
                toDateStr,
                searchKeyword: searchKeyword || "",
                withTracking: !withoutTrackingID,
                currentPage,
                pageSize
            });

            const result =
                await courierDetailsService.getCourierTrackingDetails(
                    fromDateStr,
                    toDateStr,
                    searchKeyword || "",
                    !withoutTrackingID,
                    currentPage,
                    pageSize
                );

            // Generate unique keys for each row to avoid duplicate key warnings
            // OrderID is NOT unique - same order can have multiple invoices!
            const dataWithUniqueKeys = result.items.map((item: CourierDetailsDTO) => ({
                ...item,
                uniqueKey: `${item.orderID}-${item.invoiceNo}-${item.id}`
            }));

            setData(dataWithUniqueKeys);
            setTotalRecords(result.totalCount);

            console.log('✔️ State updated - data should now have', dataWithUniqueKeys.length, 'items');

        } catch (error) {
            console.error('❌ Error loading data:', error);

            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load courier details',
                life: 3000
            });

        } finally {
            setLoading(false);
        }
    };


    /* ================= SAVE ================= */

    // Use uniqueKey (not id) so rows without a saved record (id=0) are never confused
    const autoSave = async (row: CourierDetailsDTO) => {
        const lockKey = row.uniqueKey ?? String(row.id);
        if (savingRef.current[lockKey]) return;
        savingRef.current[lockKey] = true;

        try {
            const payload = {
                orderId: row.orderID,
                invoiceNo: row.invoiceNo,
                trackingId: row.trackingID,
                shipmentDate: row.shipmentDate,
                noofPackages: row.noOfPackages,
                courieredById: row.courieredByID,
                sendMessage: false
            };
            await courierDetailsService.saveOrderCourierDetail(payload);
        } catch (err) {
            console.error('AutoSave error:', err);
        } finally {
            savingRef.current[lockKey] = false;
        }
    };

    // Called by CourierByDropdown (now outside this component)
    const updateCourierBy = (
        uniqueKey: string,
        userId: number,
        name: string | null
    ) => {
        const newData = data.map(item => {
            if (item.uniqueKey === uniqueKey) {
                const updated = { ...item, courieredByID: userId || null, courierByName: name };
                autoSave(updated);
                return updated;
            }
            return item;
        });
        setData(newData);
    };


    // Use uniqueKey to avoid matching multiple rows that share the same id (e.g. id=0)
    const handleInputChange = (
        uniqueKey: string,
        field: keyof CourierDetailsDTO,
        value: any
    ) => {
        let updatedRow: CourierDetailsDTO | null = null;

        const newData = data.map((item) => {
            if (item.uniqueKey === uniqueKey) {
                updatedRow = { ...item, [field]: value };
                return updatedRow;
            }
            return item;
        });

        setData(newData);

        if (updatedRow) {
            autoSave(updatedRow);
        }
    };

    const toLocalISODate = (date: Date) => {

        const offset = date.getTimezoneOffset() * 60000;

        return new Date(date.getTime() - offset)
            .toISOString()
            .split('T')[0];
    };

    const handleFilter = () => {
        setCurrentPage(1);
    };

    const handleReset = () => {
        setFromDate(null);
        setToDate(null);
        setSearchKeyword('');
        setWithoutTrackingID(false);
        setSelectedRows([]);
        setCurrentPage(1);
        setCourierProviderFilter([]);
        setSortField(null);
        setSortOrder(1);
    };

    // Handle column sort (client-side over current page)
    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 1 ? -1 : 1);
        } else {
            setSortField(field);
            setSortOrder(1);
        }
    };

    // Unique courier provider options from current page data
    const courierProviderOptions = useMemo(() =>
        Array.from(new Set(data.map(d => d.courierProvider).filter(Boolean))).sort() as string[]
        , [data]);

    // Filtered + sorted view of current page data
    const displayData = useMemo(() => {
        let result = [...data];

        // Apply courier provider filter
        if (courierProviderFilter.length > 0) {
            result = result.filter(r => courierProviderFilter.includes(r.courierProvider));
        }

        // Apply manual sort
        if (sortField) {
            result.sort((a, b) => {
                const aVal = (a as any)[sortField];
                const bVal = (b as any)[sortField];
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                if (typeof aVal === 'number' && typeof bVal === 'number') return sortOrder * (aVal - bVal);
                return sortOrder * String(aVal).localeCompare(String(bVal));
            });
        }

        return result;
    }, [data, courierProviderFilter, sortField, sortOrder]);

    // FilterableColumnHeader – mirrors POS movements product column style
    const FilterableColumnHeader = ({
        title,
        field,
        filterValue,
        onFilterChange,
        options,
    }: {
        title: string;
        field: string;
        filterValue: string[];
        onFilterChange: (v: string[]) => void;
        options: string[];
    }) => {
        const isActive = filterValue.length > 0;
        const isSorted = sortField === field;
        return (
            <div className="flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
                <span className="font-semibold">{title}</span>
                <div className="p-overlay-badge">
                    <MultiSelect
                        value={filterValue}
                        options={options}
                        onChange={(e) => onFilterChange(e.value)}
                        placeholder={`Filter ${title}`}
                        maxSelectedLabels={0}
                        selectedItemsLabel={`${filterValue.length} selected`}
                        className="custom-multiselect"
                        style={{ width: '40px', height: '32px' }}
                        panelStyle={{ minWidth: '200px' }}
                        display="chip"
                        pt={{
                            root: {
                                style: {
                                    border: isActive ? '2px solid #22c55e' : '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    background: isActive ? '#f0fdf4' : 'white',
                                    transition: 'all 0.3s ease',
                                }
                            },
                            trigger: { style: { width: '100%', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                            label: { style: { display: 'none' } }
                        }}
                    />
                </div>
                <Button
                    icon={isSorted && sortOrder === -1 ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'}
                    className="p-button-text p-button-sm"
                    onClick={() => handleSort(field)}
                    style={{ color: isSorted ? '#22c55e' : '#6b7280', padding: '4px 8px', minWidth: 'auto' }}
                    tooltip={`Sort ${isSorted ? (sortOrder === 1 ? 'Descending' : 'Ascending') : 'Ascending'}`}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    // Plain sortable header (no filter dropdown) for columns that only need sorting
    const SortableHeader = ({ title, field }: { title: string; field: string }) => {
        const isSorted = sortField === field;
        return (
            <div className="flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
                <span className="font-semibold">{title}</span>
                <Button
                    icon={isSorted && sortOrder === -1 ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'}
                    className="p-button-text p-button-sm"
                    onClick={() => handleSort(field)}
                    style={{ color: isSorted ? '#22c55e' : '#6b7280', padding: '4px 8px', minWidth: 'auto' }}
                    tooltip={`Sort ${isSorted ? (sortOrder === 1 ? 'Descending' : 'Ascending') : 'Ascending'}`}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };


    const handleSaveAndSend = async (rows?: CourierDetailsDTO[]) => {
        // ✅ Prevent double-clicks
        if (sendingRef.current) return;

        const rowsToSend = rows || selectedRows;

        if (rowsToSend.length === 0) {
            console.log('⚠️ No rows to send');
            toast.current?.show({
                severity: 'warn',
                summary: 'No Selection',
                detail: 'Select at least one row',
                life: 3000
            });
            return;
        }

        const currentDataMap = new Map(data.map(item => [item.uniqueKey, item]));
        const rowsWithLatestData = rowsToSend.map(row => {
            const currentRow = currentDataMap.get(row.uniqueKey);
            return currentRow || row;
        });

        const invalidRows: string[] = [];
        const validRows: CourierDetailsDTO[] = [];

        rowsWithLatestData.forEach((row) => {
            const missingFields: string[] = [];

            if (!row.trackingID || row.trackingID.trim() === '') {
                missingFields.push('Tracking Number');
            }
            if (!row.shipmentDate) {
                missingFields.push('Shipment Date');
            }
            if (!row.noOfPackages || row.noOfPackages.toString().trim() === '') {
                missingFields.push('No of Packages');
            } else {
                // Must contain both at least one digit AND at least one letter
                const val = row.noOfPackages.toString();
                const hasDigit = /\d/.test(val);
                const hasLetter = /[a-zA-Z]/.test(val);
                if (!hasDigit || !hasLetter) {
                    missingFields.push('No of Packages (must include a number and a letter, e.g. "5 box")');
                }
            }
            if (!row.courieredByID) {
                missingFields.push('Courier By');
            }

            if (missingFields.length > 0) {
                invalidRows.push(
                    `Order ${row.orderID} (Invoice ${row.invoiceNo}): Missing ${missingFields.join(', ')}`
                );
            } else {
                validRows.push(row);
            }
        });

        // If ANY rows are invalid, show error and don't send
        if (invalidRows.length > 0) {
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Failed',
                detail: 'Fill the mandatory fields',
                life: 5000
            });
            return;
        }

        // ✅ All rows are valid, proceed with sending
        sendingRef.current = true;

        try {
            // Send all messages in parallel instead of sequentially to avoid timeout
            const sendPromises = validRows.map(async (row) => {
                const payload = {
                    orderId: row.orderID,
                    invoiceNo: row.invoiceNo,                    // required to uniquely identify the invoice record
                    trackingId: row.trackingID,
                    shipmentDate: row.shipmentDate,
                    noofPackages: row.noOfPackages,
                    courieredById: row.courieredByID,
                    sendMessage: true
                };

                return courierDetailsService.saveOrderCourierDetail(payload)
                    .then(() => ({ success: true, orderID: row.orderID }))
                    .catch((err) => ({ success: false, orderID: row.orderID, error: err }));
            });

            const results = await Promise.all(sendPromises);

            const successCount = results.filter(r => r.success).length;
            const failedCount = results.filter(r => !r.success).length;

            if (failedCount === 0) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Message sent successfully for ${successCount} order${successCount > 1 ? 's' : ''}`,
                    life: 3000
                });
                // Re-sync from server so subsequent sends on the same page work correctly
                setSelectedRows([]);
                loadData();
            } else if (successCount > 0) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Partial Success',
                    detail: `${successCount} message(s) sent, ${failedCount} failed. Please try again for failed orders.`,
                    life: 5000
                });
                // Reload to get updated ids even for partial success
                loadData();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Failed to send messages. Please check your connection and try again.',
                    life: 5000
                });
            }

        } catch (error) {
            console.error('❌ Send message error:', error);

            toast.current?.show({
                severity: 'error',
                summary: 'Failed',
                detail: 'Failed to send message. Please try again.',
                life: 3000
            });
        } finally {
            sendingRef.current = false;
        }
    };


    /* ================= COLUMNS ================= */

    const trackingTemplate = (row: CourierDetailsDTO) => (
        <InputText
            value={row.trackingID || ''}
            placeholder="Enter Tracking Number *"
            style={{
                width: '100%',
                borderColor: !row.trackingID ? '#ef4444' : undefined,
                borderWidth: !row.trackingID ? '2px' : undefined
            }}
            onChange={e => handleInputChange(row.uniqueKey!, 'trackingID', e.target.value)}
        />
    );

    const dateTemplate = (row: CourierDetailsDTO) => {

        const val = row.shipmentDate
            ? new Date(row.shipmentDate)
            : null;

        return (
            <Calendar
                value={val}
                showIcon
                dateFormat="dd-mm-yy"
                className="w-full"
                placeholder='Enter date *'
                minDate={new Date()}
                style={{
                    borderColor: !row.shipmentDate ? '#ef4444' : undefined,
                    borderWidth: !row.shipmentDate ? '2px' : undefined
                }}
                onChange={e => {
                    const d = e.value as Date;
                    handleInputChange(
                        row.uniqueKey!,
                        'shipmentDate',
                        d ? toLocalISODate(d) : null
                    );
                }}
            />
        );
    };

    // Characters that are dangerous for query injection
    const BLOCKED_CHARS_REGEX = /['";\\<>&`=]/;

    const packageTemplate = (row: CourierDetailsDTO) => {
        const val = row.noOfPackages || '';
        const hasDigit = /\d/.test(val);
        const hasLetter = /[a-zA-Z]/.test(val);
        const isEmpty = val.trim() === '';
        // Valid = non-empty AND has both a digit and a letter (e.g. "5 box", "2boxes")
        const isValid = !isEmpty && hasDigit && hasLetter;
        const showError = isEmpty || !isValid;

        let borderColor: string | undefined;
        if (isEmpty) {
            borderColor = '#ef4444';           // red – empty
        } else if (!isValid) {
            borderColor = '#f97316';           // orange – missing digit or letter
        }

        const tooltipText = isEmpty
            ? 'Required'
            : !hasDigit
                ? 'Must include at least one number (e.g. "2 box")'
                : !hasLetter
                    ? 'Must include at least one letter (e.g. "5 boxes")'
                    : '';

        return (
            <InputText
                value={val}
                placeholder='e.g. 5 box *'
                className="w-full"
                style={{
                    borderColor: showError ? borderColor : undefined,
                    borderWidth: showError ? '2px' : undefined
                }}
                tooltip={showError && tooltipText ? tooltipText : undefined}
                tooltipOptions={{ position: 'top' }}
                onKeyDown={(e) => {
                    if (BLOCKED_CHARS_REGEX.test(e.key)) {
                        e.preventDefault();
                    }
                }}
                onChange={e => {
                    const sanitized = e.target.value.replace(BLOCKED_CHARS_REGEX, '');
                    handleInputChange(row.uniqueKey!, 'noOfPackages', sanitized);
                }}
            />
        );
    };


    /* ================= DROPDOWN ================= */
    // CourierByDropdown is now defined OUTSIDE this component (above CourierDetails)
    // to prevent React from treating it as a new component type on every re-render.
    const sendButtonBodyTemplate = (rowData: CourierDetailsDTO) => {
        return (
            <Button
                icon="pi pi-send"
                className="p-button-rounded p-button-success p-button-text"
                tooltip="Send Message"
                tooltipOptions={{ position: 'left' }}
                onClick={() => handleSaveAndSend([rowData])}
            />
        );
    };

    const onPageChange = (e: any) => {
        const page = e.page + 1;
        const size = e.rows;

        setCurrentPage(page);
        setPageSize(size);
    };
    // Debug: Log before rendering
    console.log('🎨 RENDERING Component with:', {
        dataLength: data.length,
        totalRecords,
        currentPage,
        pageSize,
        first: (currentPage - 1) * pageSize,
        searchKeyword,
        firstRecord: data[0] ? {
            orderID: data[0].orderID,
            customerName: data[0].customerName
        } : null
    });

    return (
        <div className="courier-details-container">
            <Toast ref={toast} />

            <div className="card p-4 w-full" style={{
                backgroundColor: '#e8f5e9',
                borderRadius: '12px'
            }}>
                <div className="flex align-items-center gap-3 mb-4" style={{
                    backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))',
                    padding: '16px',
                    borderRadius: '8px',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    marginTop: '-16px',
                    paddingLeft: '16px',
                    paddingRight: '16px'
                }}>
                    <div style={{
                        backgroundColor: 'rgb(76, 175, 80)',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '48px',
                        height: '48px'
                    }}>
                        <i className="pi pi-box" style={{ color: 'white', fontSize: '1.5rem' }}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h5 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '600', fontSize: '1.125rem' }}>
                            Courier Tracking Details
                        </h5>
                        <p className="m-0" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            Track, manage, and update courier information
                        </p>
                    </div>
                </div>

                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-filter" style={{ color: 'rgb(76, 175, 80)', fontSize: '1.1rem' }}></i>
                    <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>Filter Courier Tracking Details</span>
                </div>

                <div className="flex justify-content-between align-items-center gap-3" style={{
                    backgroundColor: 'rgb(232, 245, 233)',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '8px'
                }}>
                    <div className="flex align-items-center gap-2" style={{ flex: 1, maxWidth: '400px' }}>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" />
                            <InputText
                                type="search"
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                placeholder="Keyword Search"
                                className="w-full"
                                style={{
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    border: '1px solid #d1d5db'
                                }}
                            />
                        </span>
                    </div>

                    <div className="flex align-items-center gap-2">
                        <Calendar
                            value={fromDate}
                            onChange={(e) => setFromDate(e.value as Date)}
                            dateFormat="dd-mm-yy"
                            placeholder="From Date"
                            showIcon
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db',
                                overflow: 'hidden'
                            }}
                            inputStyle={{
                                borderRadius: '8px'
                            }}
                        />

                        <Calendar
                            value={toDate}
                            onChange={(e) => setToDate(e.value as Date)}
                            dateFormat="dd-mm-yy"
                            placeholder="To Date"
                            showIcon
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db',
                                overflow: 'hidden'
                            }}
                            inputStyle={{
                                borderRadius: '8px'
                            }}
                        />

                        <div className="flex align-items-center gap-2" style={{
                            padding: '0.5rem 0.75rem',
                            backgroundColor: withoutTrackingID ? 'rgb(76, 175, 80)' : 'white',
                            borderRadius: '8px',
                            border: `1px solid ${withoutTrackingID ? 'rgb(76, 175, 80)' : '#d1d5db'}`,
                            transition: 'all 0.3s ease'
                        }}>
                            <Checkbox
                                inputId="withoutTracking"
                                checked={withoutTrackingID}
                                onChange={(e) => setWithoutTrackingID(e.checked ?? false)}
                            />
                            <label
                                htmlFor="withoutTracking"
                                style={{
                                    margin: 0,
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    color: withoutTrackingID ? 'white' : '#2d3748',
                                    fontWeight: withoutTrackingID ? '600' : '400',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Without Tracking Number
                            </label>
                        </div>

                        {/* <Button
                            label="Filter"
                            onClick={handleFilter}
                            className="p-button-success"
                            icon="pi pi-filter"
                            style={{ borderRadius: '8px' }}
                        /> */}

                        <Button
                            label="Reset"
                            onClick={handleReset}
                            className="p-button-secondary"
                            icon="pi pi-refresh"
                            style={{ borderRadius: '8px' }}
                        />
                    </div>
                </div>
            </div>

            <div className="col-12">
                <div className="flex align-items-center gap-2 mb-3 px-2">
                    <div style={{
                        backgroundColor: 'rgb(76, 175, 80)',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <i className="pi pi-list" style={{ color: 'white', fontSize: '1.2rem' }}></i>
                    </div>
                    <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '1.1rem' }}>Courier Records</span>
                </div>

                <div className="card p-2" style={{ borderRadius: '12px' }}>

                    {selectedRows.length > 0 && (
                        <div className="flex justify-content-end mb-2 gap-2">
                            <Button
                                label="Save and Send Message"
                                icon="pi pi-send"
                                onClick={() => handleSaveAndSend()}
                                style={{
                                    backgroundColor: '#22c55e',
                                    border: 'none',
                                    color: 'white',
                                    borderRadius: '6px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.9rem'
                                }}
                                tooltip={`Send message for ${selectedRows.length} selected row${selectedRows.length !== 1 ? 's' : ''}`}
                                tooltipOptions={{ position: 'top' }}
                            />
                        </div>
                    )}
                    <div
                        ref={tableWrapperRef}
                        className={isTableScrolled ? 'courier-table-scrolled' : ''}
                    >
                        <DataTable
                            key={`datatable-${currentPage}-${pageSize}-${searchKeyword}`}
                            value={displayData}
                            dataKey="uniqueKey"
                            selection={selectedRows}
                            selectionMode="checkbox"
                            onSelectionChange={(e) =>
                                setSelectedRows(e.value as CourierDetailsDTO[])
                            }
                            paginator
                            lazy
                            totalRecords={totalRecords}
                            rows={pageSize}
                            first={(currentPage - 1) * pageSize}
                            onPage={onPageChange}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            loading={loading}
                            emptyMessage="No records found"
                            stripedRows
                            scrollable
                            scrollHeight="600px"
                            frozenWidth="300px"
                            className="courier-details-table"
                        >
                            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} frozen />
                            <Column
                                field="orderID"
                                header={<SortableHeader title="Order ID" field="orderID" />}
                                style={{ minWidth: '140px' }}
                                frozen
                            />
                            <Column
                                field="invoiceNo"
                                header={<SortableHeader title="Invoice No" field="invoiceNo" />}
                                style={{ minWidth: '140px' }}
                                frozen
                            />
                            <Column
                                field="customerName"
                                header={<SortableHeader title="Customer Name" field="customerName" />}
                                style={{ minWidth: '150px' }}
                            />
                            <Column field="customerNumber" header="Customer Number" style={{ minWidth: '150px' }} />
                            <Column
                                field="courierProvider"
                                header={
                                    <FilterableColumnHeader
                                        title="Courier Provider"
                                        field="courierProvider"
                                        filterValue={courierProviderFilter}
                                        onFilterChange={setCourierProviderFilter}
                                        options={courierProviderOptions}
                                    />
                                }
                                style={{ minWidth: '180px' }}
                            />
                            <Column
                                header="Action"
                                body={sendButtonBodyTemplate}
                                style={{ minWidth: '80px', textAlign: 'center' }}
                            />
                            <Column
                                field="trackingID"
                                header="Tracking Number *"
                                body={trackingTemplate}
                                style={{ minWidth: '180px' }}
                            />
                            <Column
                                field="shipmentDate"
                                header="Shipment Date *"
                                body={dateTemplate}
                                style={{ minWidth: '180px' }}
                            />
                            <Column
                                field="noOfPackages"
                                header="No of Packages *"
                                body={packageTemplate}
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                field="courierByName"
                                header="Courier By *"
                                body={(row) => (
                                    <CourierByDropdown
                                        rowData={row}
                                        onUpdateCourierBy={updateCourierBy}
                                    />
                                )}
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="invoiceDate"
                                header={<SortableHeader title="Invoice Date" field="invoiceDate" />}
                                style={{ minWidth: '140px' }}
                            />
                            <Column field="courierContactPerson" header="Courier Contact Person" style={{ minWidth: '180px' }} />
                            <Column field="courierContactNumber" header="Courier Contact Number" style={{ minWidth: '180px' }} />
                        </DataTable>
                    </div>

                    <style>{`
                        .courier-details-table .p-datatable-thead > tr > th {
                            background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
                            border-bottom: 2px solid #e5e7eb;
                            padding: 1rem 0.75rem;
                            font-weight: 600;
                            color: #2d3748;
                        }

                        .courier-details-table .p-datatable-thead > tr > th:hover {
                            background: linear-gradient(to bottom, #f0fdf4, #dcfce7);
                            transition: background 0.3s ease;
                        }

                        .courier-details-table .p-datatable-tbody > tr > td {
                            padding: 0.75rem;
                        }

                        .courier-details-table .p-datatable-tbody > tr:hover {
                            background: #f9fafb;
                        }

                        .courier-details-table .p-inputtext,
                        .courier-details-table .p-inputnumber-input,
                        .courier-details-table .p-calendar {
                            border-radius: 6px;
                        }

                        /* Frozen-column right-edge indicator – visible only when table is scrolled */
                        .courier-table-scrolled .courier-details-table .p-datatable-thead > tr > th:nth-child(3),
                        .courier-table-scrolled .courier-details-table .p-datatable-tbody > tr > td:nth-child(3) {
                            border-right: 2.5px solid rgba(34, 197, 94, 0.65) !important;
                            box-shadow: 4px 0 8px rgba(34, 197, 94, 0.18);
                            transition: border-right 0.25s ease, box-shadow 0.25s ease;
                        }
                    `}</style>
                </div>
            </div>
        </div>
    );
};

export default CourierDetails;